import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  createSegment,
  deleteSegment,
  getSegmentById,
  getSegmentFields,
  getSegments,
  previewSavedSegment,
  previewSegmentRules,
  updateSegment,
} from '../services/segments.service';
import { generateSegmentDraft } from '../services/segment-ai.service';
import type { SegmentAiDraft } from '../types/segment-ai.types';
import type {
  CreateSegmentInput,
  GetSegmentsParams,
  Segment,
  SegmentFieldDefinition,
  SegmentPreviewResult,
  SegmentRuleGroup,
  SegmentsPagingResult,
  UpdateSegmentInput,
} from '../types/segments.types';

const invalidateSegments = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['segments'] });
  queryClient.invalidateQueries({ queryKey: ['segment'] });
};

export const useGetSegments = (params: GetSegmentsParams = {}, options: { enabled?: boolean } = {}) => {
  return useGlobalQuery<SegmentsPagingResult>({
    queryKey: ['segments', params],
    queryFn: () => getSegments(params),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: options.enabled ?? true,
  });
};

export const useGetSegmentById = (segmentId: string, options: { enabled?: boolean } = {}) => {
  return useGlobalQuery<Segment>({
    queryKey: ['segment', segmentId],
    queryFn: () => getSegmentById(segmentId),
    staleTime: 30 * 1000,
    enabled: (options.enabled ?? true) && Boolean(segmentId),
  });
};

export const useGetSegmentFields = (options: { enabled?: boolean } = {}) => {
  return useGlobalQuery<SegmentFieldDefinition[]>({
    queryKey: ['segment-fields'],
    queryFn: () => getSegmentFields(),
    staleTime: 60 * 1000,
    enabled: options.enabled ?? true,
  });
};

export const useCreateSegment = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<Segment, Error, CreateSegmentInput>({
    mutationFn: createSegment,
    suppressToast: true,
    onSuccess: () => {
      invalidateSegments(queryClient);
    },
  });
};

export const useUpdateSegment = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    Segment,
    Error,
    { segmentId: string; input: UpdateSegmentInput }
  >({
    mutationFn: ({ segmentId, input }) => updateSegment(segmentId, input),
    suppressToast: true,
    onSuccess: () => {
      invalidateSegments(queryClient);
    },
  });
};

export const useDeleteSegment = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<true, Error, string>({
    mutationFn: deleteSegment,
    suppressToast: true,
    onSuccess: () => {
      invalidateSegments(queryClient);
    },
  });
};

export const usePreviewSavedSegment = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<SegmentPreviewResult, Error, string>({
    mutationFn: previewSavedSegment,
    suppressToast: true,
    onSuccess: () => {
      invalidateSegments(queryClient);
    },
  });
};

export const useGenerateSegmentDraft = () => {
  return useGlobalMutation<
    SegmentAiDraft,
    Error,
    { prompt: string; fields: SegmentFieldDefinition[] }
  >({
    mutationFn: generateSegmentDraft,
    suppressToast: true,
  });
};

export const usePreviewSegmentRules = () => {
  return useGlobalMutation<
    SegmentPreviewResult,
    Error,
    { rules: SegmentRuleGroup; sampleSize?: number }
  >({
    mutationFn: previewSegmentRules,
    suppressToast: true,
  });
};
